import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { tr } from '../lib/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, uploadFile } from '../api/client';
import { useLookups } from '../api/lookups';
import type { PropertyDetails } from '../api/types';
import { Button, FileButton } from '../components/button';
import { ConfirmDialog, ErrorState } from '../components/common';
import { DetailSkeleton } from '../components/skeletons';
import { DetailHeader, InfoRow, NotesCard, PropertyStatusChip, SectionCard, SourceChip } from '../components/org';
import { PropertyMapCard } from '../components/PropertyMapCard';
import { MarketCard } from '../components/MarketCard';
import { InputAdornment, TextField } from '../components/text-field';
import { useToast } from '../components/Toast';
import { ShareProperty } from '../components/ShareProperty';
import { apiLabel, fmtDate, fmtNum, money, PROPERTY_STATUS } from '../lib/labels';
import { AttachmentsCard, FormAnswersCard } from '../components/EntryAnswers';
import { MatchingRequestsCard } from '../components/MatchesCard';
import { FeatureChips } from '../components/FeatureChips';
import { FeaturesPicker } from '@re/form-ui';
import { getUiLanguage } from '../lib/i18n';

const waLink = (phone: string) => `https://wa.me/${phone.replace(/\D/g, '')}`;

export function PropertyDetailsPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const key = ['property', id];
  const q = useQuery({ queryKey: key, queryFn: () => api<PropertyDetails>(`/properties/${id}`) });
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uploading, setUploading] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['properties'] });
  };
  const onConflict = (e: unknown) => {
    if (e instanceof ApiError && e.status === 409) {
      toast(tr('تم تعديل العقار من مستخدم آخر — تم تحديث البيانات', 'The property was changed by another user — data has been refreshed'), 'warning');
      refresh();
    } else toastError(e);
  };

  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const status = useMutation({
    mutationFn: (v: { s: string; reason?: string }) => api(`/properties/${id}/status`, { method: 'POST', body: { status: v.s, version: q.data!.version, ...(v.reason ? { reason: v.reason } : {}) } }),
    onSuccess: (_, v) => {
      const wasPending = q.data?.status === 'PENDING_REVIEW';
      toast(wasPending && v.s === 'AVAILABLE' ? tr('تم اعتماد العقار — صار متاحاً ودخل المطابقة', 'Approved — the property is available and in matching')
        : wasPending && v.s === 'WITHDRAWN' ? tr('تم رفض العرض', 'Offer rejected')
        : tr('تم تغيير الحالة', 'Status updated'));
      setRejecting(false);
      refresh();
      void qc.invalidateQueries({ queryKey: ['properties-pending-count'] });
      void qc.invalidateQueries({ queryKey: ['notes'] });
    },
    onError: onConflict,
  });
  const del = useMutation({
    mutationFn: () => api(`/properties/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast(tr('تم حذف العقار', 'Property deleted')); void qc.invalidateQueries({ queryKey: ['properties'] }); nav('/properties', { replace: true }); },
    onError: toastError,
  });
  const removeMedia = useMutation({
    mutationFn: (mediaId: string) => api(`/properties/${id}/media/${mediaId}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: toastError,
  });

  const addImages = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const ids: string[] = [];
      for (const f of Array.from(files).slice(0, 15)) ids.push(await uploadFile(f));
      await api(`/properties/${id}/media`, { method: 'POST', body: { fileIds: ids } });
      toast(tr(`تمت إضافة ${ids.length} صورة`, `${ids.length} photos added`));
      refresh();
    } catch (e) {
      toastError(e);
    } finally {
      setUploading(false);
    }
  };

  if (q.isLoading) return <DetailSkeleton />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const p = q.data!;
  const location = [p.governorate_name, p.area_name, p.neighborhood_name].filter(Boolean).join(' / ');

  return (
    <>
      <DetailHeader
        onBack={() => nav(-1)}
        title={`${apiLabel(p.request_type_name)} · ${apiLabel(p.property_type_name)}`}
        badges={<><PropertyStatusChip status={p.status} /><SourceChip source={p.source} /></>}
        meta={<><span dir="ltr" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.ref_no}</span>{location && <> · {location}</>}</>}
        actions={
          <>
            <ShareProperty
              id={p.id}
              refNo={p.ref_no}
              title={`${apiLabel(p.request_type_name)} · ${apiLabel(p.property_type_name)}${location ? ` — ${location}` : ''}`}
              share={p.share}
              available={p.status === 'AVAILABLE' || p.status === 'RESERVED'}
            />
            {p.can.changeStatus && <Button startIcon={<SwapHorizIcon />} variant="contained" onClick={(e) => setStatusAnchor(e.currentTarget)} loading={status.isPending}>{tr('تغيير الحالة', 'Change status')}</Button>}
            {(p.can.update || p.can.delete) && (
              <Tooltip title={tr('المزيد', 'More')}>
                <IconButton onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label={tr('المزيد من الإجراءات', 'More actions')} sx={{ border: 1, borderColor: 'divider' }}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
      />
      <Menu anchorEl={statusAnchor} open={!!statusAnchor} onClose={() => setStatusAnchor(null)}>
        {Object.entries(PROPERTY_STATUS).map(([k, v]) => (
          <MenuItem key={k} selected={k === p.status} disabled={k === p.status} onClick={() => { setStatusAnchor(null); status.mutate({ s: k }); }} sx={{ minWidth: 220 }}>
            <ListItemIcon>{k === p.status ? <CheckIcon color="primary" /> : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'divider' }} />}</ListItemIcon>
            <ListItemText primary={v} />
          </MenuItem>
        ))}
      </Menu>
      <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
        {p.can.update && (
          <MenuItem onClick={() => { setMoreAnchor(null); setEditing(true); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={tr('تعديل بيانات العقار', 'Edit property')} />
          </MenuItem>
        )}
        {p.can.update && p.can.delete && <Divider />}
        {p.can.delete && (
          <MenuItem onClick={() => { setMoreAnchor(null); setConfirmDelete(true); }} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={tr('حذف العقار', 'Delete property')} />
          </MenuItem>
        )}
      </Menu>

      {p.status === 'PENDING_REVIEW' && (
        <Alert
          severity="warning"
          sx={{ mb: 2, alignItems: 'center', '& .MuiAlert-message': { flex: 1 } }}
          action={p.can.changeStatus ? (
            <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
              <Button variant="contained" startIcon={<CheckIcon />} onClick={() => status.mutate({ s: 'AVAILABLE' })} loading={status.isPending && !rejecting}>{tr('اعتماد', 'Approve')}</Button>
              <Button variant="outlined" color="error" onClick={() => { setReason(''); setRejecting(true); }}>{tr('رفض', 'Reject')}</Button>
            </Stack>
          ) : undefined}
        >
          <Typography variant="body2" fontWeight={600}>{tr('عرض بانتظار الاعتماد', 'Offer waiting for approval')}</Typography>
          <Typography variant="body2">
            {tr('وصل من الرابط العام. لا يدخل المطابقة ولا يظهر كعقار متاح حتى تعتمده. راجع البيانات وعدّلها إن لزم، ثم اعتمده أو ارفضه.', 'It came through the public link. It is not matched or listed as available until you approve it. Review and edit it if needed, then approve or reject it.')}
          </Typography>
        </Alert>
      )}
      <Dialog open={rejecting} onClose={() => setRejecting(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tr('رفض العرض', 'Reject offer')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {tr('لن يُحذف العقار؛ تصبح حالته «مسحوب» ويُحفظ السبب في الملاحظات.', 'The property is not deleted; its status becomes Withdrawn and the reason is saved in the notes.')}
          </Typography>
          <TextField autoFocus fullWidth multiline minRows={2} label={tr('السبب (اختياري)', 'Reason (optional)')} value={reason} onChange={(e) => setReason(e.target.value)} slotProps={{ htmlInput: { maxLength: 1000 } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(false)}>{tr('إلغاء', 'Cancel')}</Button>
          <Button variant="contained" color="error" loading={status.isPending} onClick={() => status.mutate({ s: 'WITHDRAWN', reason: reason.trim() || undefined })}>{tr('رفض العرض', 'Reject offer')}</Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={2}>
            <SectionCard title={tr('تفاصيل العقار', 'Property details')}>
              <InfoRow label={tr('السعر', 'Price')}>{money(p.price, p.currency)}</InfoRow>
              <InfoRow label={tr('الفئة', 'Category')}>{apiLabel(p.category_name)}</InfoRow>
              {p.area != null && <InfoRow label={tr('المساحة', 'Area')}>{fmtNum(p.area)} m²</InfoRow>}
              {p.building_area != null && <InfoRow label={tr('مساحة البناء', 'Building area')}>{fmtNum(p.building_area)} m²</InfoRow>}
              {p.land_area != null && <InfoRow label={tr('مساحة الأرض', 'Land area')}>{fmtNum(p.land_area)} m²</InfoRow>}
              {p.outer_area != null && <InfoRow label={tr('المساحة الخارجية', 'Outer area')}>{fmtNum(p.outer_area)} m²</InfoRow>}
              {p.rooms != null && <InfoRow label={tr('الغرف', 'Rooms')}>{p.rooms}</InfoRow>}
              {p.bathrooms != null && <InfoRow label={tr('الحمامات', 'Bathrooms')}>{p.bathrooms}</InfoRow>}
              {p.floor != null && <InfoRow label={tr('الطابق', 'Floor')}>{p.floor}</InfoRow>}
              <InfoRow label={tr('الموقع', 'Location')}>{location}</InfoRow>
              {p.address_details && <InfoRow label={tr('تفاصيل العنوان', 'Address details')}>{p.address_details}</InfoRow>}
              {p.features.length > 0 && (
                <Box sx={{ pt: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{tr('المميزات', 'Features')}</Typography>
                  <FeatureChips features={p.features} />
                </Box>
              )}
            </SectionCard>

            <SectionCard
              title={`${tr('الصور', 'Photos')} (${p.media.length})`}
              action={p.can.update && (
                <FileButton
                  size="small"
                  startIcon={<AddPhotoAlternateIcon />}
                  loading={uploading}
                  inputProps={{
                    accept: 'image/*',
                    multiple: true,
                    onChange: (e) => {
                      void addImages(e.target.files);
                      e.target.value = '';
                    },
                  }}
                >
                  {tr('إضافة صور', 'Add photos')}
                </FileButton>
              )}
            >
              {p.media.length === 0 ? <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>{tr('لا توجد صور', 'No photos')}</Typography> : (
                <ImageList cols={3} gap={10} sx={{ m: 0 }}>
                  {p.media.map((m) => (
                    <ImageListItem key={m.id} sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
                      <a href={m.url} target="_blank" rel="noreferrer"><img src={m.url} alt="" loading="lazy" style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} /></a>
                      <ImageListItemBar
                        sx={{ bgcolor: 'rgba(31,31,31,.72)' }}
                        title={m.is_cover ? <Stack direction="row" alignItems="center" spacing={0.5}><StarIcon fontSize="inherit" /><span>{tr('الغلاف', 'Cover')}</span></Stack> : ''}
                        actionIcon={p.can.update && <IconButton size="small" sx={{ color: 'white' }} onClick={() => removeMedia.mutate(m.id)}><DeleteIcon fontSize="small" /></IconButton>}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </SectionCard>
            <MarketCard propertyId={p.id} />
            {p.status === 'AVAILABLE' && <MatchingRequestsCard propertyId={p.id} />}
            <FormAnswersCard fields={p.form_fields ?? []} answers={p.submission?.answers} currency={p.currency} />
            <AttachmentsCard files={p.files ?? []} fields={p.form_fields ?? []} edit={p.can.update ? { path: `/properties/${p.id}`, onChanged: refresh } : undefined} />
            <NotesCard entityType="PROPERTY" entityId={p.id} />
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2}>
            <PropertyMapCard p={p} onSaved={refresh} onConflict={onConflict} />
            <SectionCard title={tr('المالك', 'Owner')}>
              {p.owner ? (
                <>
                  <InfoRow label={tr('الاسم', 'Name')}>{p.owner.full_name}</InfoRow>
                  <InfoRow label={tr('الهاتف', 'Phone')}><span dir="ltr">{p.owner.phone}</span></InfoRow>
                  {p.owner.email && <InfoRow label={tr('البريد', 'Email')}>{p.owner.email}</InfoRow>}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" variant="outlined" startIcon={<PhoneIcon />} href={`tel:${p.owner.phone}`}>{tr('اتصال', 'Call')}</Button>
                    <Button size="small" variant="outlined" color="success" startIcon={<WhatsAppIcon />} href={waLink(p.owner.phone)} target="_blank">{tr('واتساب', 'WhatsApp')}</Button>
                  </Stack>
                </>
              ) : <Typography color="text.secondary">{tr('ملك الشركة — بدون مالك خارجي', 'Company-owned — no outside owner')}</Typography>}
            </SectionCard>
            <SectionCard title={tr('السجل', 'Record history')}>
              <InfoRow label={tr('أدخله', 'Created by')}>{p.created_by_name ?? tr('العميل (رابط عام)', 'Customer (public link)')}</InfoRow>
              <InfoRow label={tr('المسؤول', 'Assignee')}>{p.assigned_to_name ?? '—'}</InfoRow>
              <InfoRow label={tr('تاريخ الإدخال', 'Created at')}>{fmtDate(p.created_at)}</InfoRow>
              <InfoRow label={tr('آخر تعديل', 'Last updated')}>{fmtDate(p.updated_at)}</InfoRow>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      {editing && <EditPropertyDialog p={p} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} onConflict={onConflict} />}
      <ConfirmDialog open={confirmDelete} danger title={tr('حذف العقار', 'Delete property')} text={tr(`سيتم حذف العقار ${p.ref_no}. لا يمكن التراجع من الواجهة.`, `Property ${p.ref_no} will be deleted. This cannot be undone here.`)} confirmLabel={tr('حذف', 'Delete')} busy={del.isPending} onConfirm={() => del.mutate()} onClose={() => setConfirmDelete(false)} />
    </>
  );
}

const numOrNull = (s: string) => (s.trim() === '' ? null : Number(s));

function EditPropertyDialog({ p, onClose, onSaved, onConflict }: { p: PropertyDetails; onClose: () => void; onSaved: () => void; onConflict: (e: unknown) => void }) {
  const lookups = useLookups();
  const [v, setV] = useState({
    price: p.price ?? '',
    area: p.area ?? '',
    buildingArea: p.building_area ?? '',
    landArea: p.land_area ?? '',
    outerArea: p.outer_area ?? '',
    rooms: p.rooms?.toString() ?? '',
    bathrooms: p.bathrooms?.toString() ?? '',
    floor: p.floor?.toString() ?? '',
    addressDetails: p.address_details ?? '',
  });
  const [features, setFeatures] = useState<number[]>(p.features.map((f) => f.id));
  const save = useMutation({
    mutationFn: () =>
      api(`/properties/${p.id}`, {
        method: 'PATCH',
        body: {
          version: p.version,
          price: numOrNull(v.price),
          area: numOrNull(v.area),
          buildingArea: numOrNull(v.buildingArea),
          landArea: numOrNull(v.landArea),
          outerArea: numOrNull(v.outerArea),
          rooms: numOrNull(v.rooms),
          bathrooms: numOrNull(v.bathrooms),
          floor: numOrNull(v.floor),
          addressDetails: v.addressDetails.trim() || null,
          featureIds: features,
        },
      }),
    onSuccess: onSaved,
    onError: onConflict,
  });
  const field = (k: keyof typeof v, label: string, unit?: string) => (
    <Grid size={{ xs: 6, sm: 4 }}>
      <TextField fullWidth type="number" label={label} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} slotProps={{ input: unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined }} />
    </Grid>
  );
  // the features of this property's type, grouped, with details (same picker as the form)
  const allFeatures = lookups.data?.features ?? [];
  const ptypeId = lookups.data?.propertyTypes.find((t) => t.code === p.property_type_code)?.id;
  const available = new Set(allFeatures.filter((f) => !f.property_type_ids.length || (ptypeId !== undefined && f.property_type_ids.includes(ptypeId))).map((f) => f.code));
  const codeOf = new Map(allFeatures.map((f) => [f.id, f.code]));
  // saved features that are no longer offered (switched off) are kept as they are
  const kept = features.filter((id) => !available.has(codeOf.get(id) ?? ''));
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{tr(`تعديل العقار ${p.ref_no}`, `Edit property ${p.ref_no}`)}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {field('price', tr('السعر', 'Price'), p.currency)}
          {field('area', tr('المساحة', 'Area'), 'm²')}
          {field('buildingArea', tr('مساحة البناء', 'Building area'), 'm²')}
          {field('landArea', tr('مساحة الأرض', 'Land area'), 'm²')}
          {field('outerArea', tr('المساحة الخارجية', 'Outer area'), 'm²')}
          {field('rooms', tr('الغرف', 'Rooms'))}
          {field('bathrooms', tr('الحمامات', 'Bathrooms'))}
          {field('floor', tr('الطابق', 'Floor'))}
          <Grid size={12}>
            <TextField fullWidth multiline minRows={2} label={tr('تفاصيل العنوان', 'Address details')} value={v.addressDetails} onChange={(e) => setV({ ...v, addressDetails: e.target.value })} />
          </Grid>
          <Grid size={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{tr('المميزات', 'Features')}</Typography>
            {lookups.data && (
              <FeaturesPicker
                lang={getUiLanguage()}
                lookups={lookups.data}
                available={available}
                value={features.map((id) => codeOf.get(id)).filter((c): c is string => !!c && available.has(c))}
                onChange={(v) => {
                  const codes = new Set((v as string[] | undefined) ?? []);
                  setFeatures([...kept, ...allFeatures.filter((f) => codes.has(f.code)).map((f) => f.id)]);
                }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tr('إلغاء', 'Cancel')}</Button>
        <Button variant="contained" onClick={() => save.mutate()} loading={save.isPending}>{tr('حفظ', 'Save')}</Button>
      </DialogActions>
    </Dialog>
  );
}
